import React, { useState } from 'react';
import { Mail, Loader2, Clock, CheckCircle, X } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * SessionExpiredModal - Shows when user's session expires
 * Automatically sends a magic link to the user's email
 */
export const SessionExpiredModal = ({ isOpen, email, onClose }) => {
  const [status, setStatus] = useState('idle'); // idle, sending, sent, error
  const [errorMessage, setErrorMessage] = useState('');

  const handleSendMagicLink = async () => {
    if (!email) {
      // If no email, just redirect to login
      localStorage.removeItem('portal_token');
      localStorage.removeItem('portal_user');
      window.location.href = '/login';
      return;
    }

    setStatus('sending');
    setErrorMessage('');

    try {
      await axios.post(
        `${API_URL}/api/auth/request-magic-link`,
        null,
        { params: { email } }
      );
      setStatus('sent');
    } catch (error) {
      setStatus('error');
      setErrorMessage(error.response?.data?.detail || 'Error al enviar el enlace');
    }
  };

  const handleGoToLogin = () => {
    localStorage.removeItem('portal_token');
    localStorage.removeItem('portal_user');
    localStorage.removeItem('worker_token');
    window.location.href = '/login';
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 animate-in fade-in zoom-in duration-200">
        {/* Close button */}
        <button
          onClick={handleGoToLogin}
          className="absolute top-4 right-4 p-2 hover:bg-gray-100 rounded-full transition-colors"
          aria-label="Cerrar"
        >
          <X className="w-5 h-5 text-gray-400" />
        </button>

        {/* Content based on status */}
        {status === 'idle' && (
          <>
            <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Clock className="w-8 h-8 text-amber-600" />
            </div>
            
            <h2 className="text-xl font-semibold text-gray-900 text-center mb-2">
              Tu sesión ha expirado
            </h2>
            
            <p className="text-gray-500 text-center mb-6">
              Por tu seguridad, tu sesión ha expirado después de un período de inactividad.
            </p>

            {email && (
              <p className="text-sm text-gray-400 text-center mb-4">
                Te enviaremos un nuevo enlace de acceso a:<br />
                <span className="font-medium text-gray-600">{email}</span>
              </p>
            )}

            <div className="space-y-3">
              <button
                onClick={handleSendMagicLink}
                className="w-full bg-gradient-to-r from-[#e03c3c] to-[#f47c2a] hover:from-[#c73535] hover:to-[#e06a1f] text-white py-3 rounded-xl font-medium flex items-center justify-center gap-2 transition-all shadow-lg shadow-[#e03c3c]/20"
                data-testid="send-magic-link-btn"
              >
                <Mail className="w-5 h-5" />
                Enviar enlace de acceso
              </button>
              
              <button
                onClick={handleGoToLogin}
                className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 py-3 rounded-xl font-medium transition-colors"
              >
                Ir a la página de login
              </button>
            </div>
          </>
        )}

        {status === 'sending' && (
          <div className="py-8 text-center">
            <Loader2 className="w-12 h-12 text-[#e03c3c] animate-spin mx-auto mb-4" />
            <p className="text-gray-600">Enviando enlace de acceso...</p>
          </div>
        )}

        {status === 'sent' && (
          <>
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            
            <h2 className="text-xl font-semibold text-gray-900 text-center mb-2">
              ¡Enlace enviado!
            </h2>
            
            <p className="text-gray-500 text-center mb-2">
              Revisa tu correo electrónico:
            </p>
            
            <p className="font-medium text-gray-900 text-center mb-6">
              {email}
            </p>

            <p className="text-sm text-gray-400 text-center mb-6">
              Haz clic en el enlace del correo para continuar donde lo dejaste.
              El enlace es válido por 8 horas.
            </p>

            <button
              onClick={handleGoToLogin}
              className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 py-3 rounded-xl font-medium transition-colors"
            >
              Entendido
            </button>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <X className="w-8 h-8 text-red-600" />
            </div>
            
            <h2 className="text-xl font-semibold text-gray-900 text-center mb-2">
              Error al enviar el enlace
            </h2>
            
            <p className="text-gray-500 text-center mb-6">
              {errorMessage || 'No pudimos enviar el enlace de acceso. Por favor intenta de nuevo.'}
            </p>

            <div className="space-y-3">
              <button
                onClick={() => setStatus('idle')}
                className="w-full bg-gradient-to-r from-[#e03c3c] to-[#f47c2a] text-white py-3 rounded-xl font-medium transition-all"
              >
                Intentar de nuevo
              </button>
              
              <button
                onClick={handleGoToLogin}
                className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 py-3 rounded-xl font-medium transition-colors"
              >
                Ir a la página de login
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SessionExpiredModal;
